import {
  Button,
  ButtonMode,
  ButtonSize,
  Checkbox,
  ContentTypeTag,
  Filter,
  Icon,
  ICONS,
  Popup,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { capitalize } from 'lodash';
import React, { useMemo } from 'react';
import useIssuersFilterContext from '../../../hooks/filters/useIssuersFilterContext';
import {
  CSViewOption,
  DateFilterOption,
  FILTER_TYPE_LABELS_SINGULAR,
  IssuersFilterOption,
  KeywordFilterOption,
} from '../../../models';
import { convertToTreeModel } from '../../../utils/filterUtils';
import { getApiFilterTypes } from '../../FiltersBar/FiltersBar.logic';
import { COMPANY_FILTER_GROUPS, COVERAGE_ITEMS, CS_VIEW_DATA } from '../Filters.constants';
import styles from './IssuersQuickFiltersRow.module.scss';
import useFetchFiltersWithEntitiy from '../../../hooks/tags/useFetchFiltersWithEntitiy';

const cx = classNames.bind(styles);

const { quickFilters } = COMPANY_FILTER_GROUPS;

interface IssuersFiltersRowProps {
  coverageAndOrEnabled: boolean;
}

const IssuersQuickFiltersRow = ({ coverageAndOrEnabled }: IssuersFiltersRowProps): React.ReactElement | null => {
  const {
    activeFilters,
    setTags,
    setRecWeightings,
    toggleCoverage,
    resetFilters,
    setSearchText,
    setCoverageOperation,
  } = useIssuersFilterContext();

  const apiFilterTypes = getApiFilterTypes(quickFilters);
  const { data: fetchedFilters = [], isLoading } = useFetchFiltersWithEntitiy(apiFilterTypes, 'company');

  const filters = useMemo(
    () =>
      fetchedFilters.map((filter) => ({
        type: filter.type,
        options: convertToTreeModel(filter),
      })),
    [fetchedFilters]
  );

  const renderSearchInput = () => (
    <div className={cx('search-input-container')}>
      <div className={cx('left-icon')} data-testid="search-icon">
        <Icon name={ICONS.Search} height={12} width={12} />
      </div>
      <input
        className={cx('search-input')}
        value={activeFilters.searchText || ''}
        autoComplete="off"
        aria-label="Search Companies"
        maxLength={200}
        onChange={(event) => setSearchText(event.target.value)}
        placeholder="Search Companies"
        data-testid="search-input"
      />
      {activeFilters.searchText && (
        <Button
          ariaLabel="clear input"
          className={cx('clear-button')}
          dataTestId="search-clear-button"
          onClick={() => setSearchText('')}
        >
          <Icon name={ICONS.Clear} width={12} height={12} />
        </Button>
      )}
    </div>
  );

  const renderCoverageItems = () => (
    <div className={cx('coverage-popup')}>
      {coverageAndOrEnabled ? (
        <div className={cx('coverage-popup-header')}>
          <div className={cx('coverage-filter-type')}>
            {(['and', 'or'] as ('and' | 'or')[]).map((item) => (
              <span
                key={item}
                onClick={() => setCoverageOperation(item)}
                className={cx('coverage-filter-type-item', { active: activeFilters.coverageOperation === item })}
              >
                {capitalize(item)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <div className={cx('coverage-popup-body')}>
        {COVERAGE_ITEMS.map((item) => (
          <div key={item.id} className={cx('coverage-popup-body-item')}>
            <Checkbox
              checked={(activeFilters.coverage || []).includes(item.id)}
              onChange={() => toggleCoverage(item.id)}
              dataTestId={`coverage-check-${item.id}`}
              label={item.contentType}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderFilterOption = (filterType: IssuersFilterOption) => {
    if (filterType === DateFilterOption || filterType === KeywordFilterOption) {
      return null;
    }

    if (filterType === CSViewOption) {
      return (
        <Filter
          data={CS_VIEW_DATA}
          placeholder="CS View"
          dataTestId="filter-box"
          onChange={setRecWeightings}
          checkedKeys={activeFilters.recWeightings || []}
          key={filterType}
          popupClassName={cx('popup')}
        />
      );
    }

    const filter = filters.find((feedFilter) => feedFilter.type === filterType);

    if (!filter) {
      return null;
    }

    return (
      <Filter
        data={filter.options}
        placeholder={FILTER_TYPE_LABELS_SINGULAR[filter.type]}
        dataTestId="filter-box"
        onChange={(tags) => setTags(filterType, tags)}
        checkedKeys={activeFilters.tags?.[filterType] || []}
        key={filterType}
        popupClassName={cx('popup')}
      />
    );
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className={cx('body')}>
      {renderSearchInput()}
      {quickFilters.map(renderFilterOption)}
      <Popup
        on="click"
        position="bottom left"
        trigger={
          <Button className={cx('coverage-buttons')} mode={ButtonMode.SECONDARY} size={ButtonSize.X_SMALL}>
            {COVERAGE_ITEMS.map((item) => (
              <div
                key={item.id}
                data-testid={item.testId}
                className={cx('coverage-icon', { unselected: !(activeFilters.coverage || []).includes(item.id) })}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleCoverage(item.id);
                }}
              >
                <ContentTypeTag contentType={item.contentType} short />
              </div>
            ))}
            <div className={cx('coverage-caret')}>
              <Icon name={ICONS.CaretDown} height={8} width={8} />
            </div>
          </Button>
        }
        content={renderCoverageItems()}
      />
      <Button
        mode={ButtonMode.TERTIARY}
        size={ButtonSize.SMALL}
        onClick={resetFilters}
        dataTestId="reset-filters-button"
      >
        <Icon name={ICONS.Clear} width={12} height={12} />
        Reset
      </Button>
    </div>
  );
};

export default IssuersQuickFiltersRow;
